import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import { createRouterLinkClickHandler } from '../../../../hooks/useRouterLinkClick';
import { useSpacePath } from '../../../../hooks/useSpacePath';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useGetProvidersCount } from '../../api/useGetProvidersCount';
import { useGetProvidersPaged } from '../../api/useGetProvidersPaged';
import { useGetProviderTags } from '../../api/useGetProviderTags';
import { PROVIDERS_PAGE_SIZE_OPTIONS } from '../../constants/pagination';

/** Below this size the directory fits on one screen and a search field is only noise. */
const SEARCH_MIN_PROVIDERS = 6;

const DEFAULT_PAGE_SIZE = PROVIDERS_PAGE_SIZE_OPTIONS[0];

export function useProvidersList() {
  const navigate = useNavigate();
  const spacePath = useSpacePath();
  const { isAdmin } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [underContractOnly, setUnderContractOnly] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const debouncedSearch = useDebouncedValue(searchTerm.trim());

  const { data: tags = [] } = useGetProviderTags();

  // The page belongs to the active filter set (search + tags + underContract)
  const [pagination, setPagination] = useState({
    search: '',
    tagIds: [] as string[],
    underContract: false,
    page: 0,
  });

  const isTagIdsEqual =
    pagination.tagIds.length === selectedTagIds.length &&
    pagination.tagIds.every((id) => selectedTagIds.includes(id));

  const isCurrentFilter =
    pagination.search === debouncedSearch &&
    isTagIdsEqual &&
    pagination.underContract === underContractOnly;

  const page = isCurrentFilter ? pagination.page : 0;

  function setPage(nextPage: number) {
    setPagination({
      search: debouncedSearch,
      tagIds: selectedTagIds,
      underContract: underContractOnly,
      page: nextPage,
    });
  }

  const { data, isLoading } = useGetProvidersPaged({
    search: debouncedSearch,
    page,
    pageSize,
    tagIds: selectedTagIds,
    underContractOnly,
  });

  // Unfiltered total: drives the empty state and whether searching is worth it.
  const { data: providersCount = 0, isLoading: isCountLoading } = useGetProvidersCount();

  const providers = data?.rows ?? [];
  const total = data?.total ?? 0;

  // A deletion or filter change can empty the last page: step back to the new last one.
  useEffect(() => {
    if (data && page > 0 && data.rows.length === 0 && total > 0) {
      setPage(Math.ceil(total / pageSize) - 1);
    }
  }, [data, page, pageSize, total]);

  function handlePageSizeChange(nextPageSize: number) {
    setPageSize(nextPageSize);
    setPage(0);
  }

  function handleToggleTag(tagId: string) {
    setSelectedTagIds((current) =>
      current.includes(tagId)
        ? current.filter((id) => id !== tagId)
        : [...current, tagId]
    );
  }

  function handleToggleUnderContract() {
    setUnderContractOnly((current) => !current);
  }

  function handleClearFilters() {
    setSelectedTagIds([]);
    setUnderContractOnly(false);
  }

  function handleClearAllFilters() {
    setSearchTerm('');
    setSelectedTagIds([]);
    setUnderContractOnly(false);
  }

  function getProviderHref(providerId: string) {
    return spacePath(`/providers/${providerId}`);
  }

  function getProviderClickHandler(providerId: string) {
    return createRouterLinkClickHandler(navigate, getProviderHref(providerId));
  }

  const noResultSubtitle = (() => {
    if (debouncedSearch) {
      return `Aucun prestataire ne correspond à « ${debouncedSearch} ».`;
    }
    if (selectedTagIds.length > 0) {
      const selectedNames = tags
        .filter((t) => selectedTagIds.includes(t.id))
        .map((t) => t.name)
        .join(', ');
      return `Aucun prestataire pour les tags : ${selectedNames}.`;
    }
    if (underContractOnly) {
      return 'Aucun prestataire sous contrat en cours.';
    }
    return 'Aucun prestataire ne correspond aux critères.';
  })();

  const showFilters = tags.length > 0 || providersCount > 0;

  return {
    isAdmin,
    isLoading,
    searchTerm,
    setSearchTerm,
    tags,
    selectedTagIds,
    underContractOnly,
    handleToggleTag,
    handleToggleUnderContract,
    handleClearFilters,
    handleClearAllFilters,
    providers,
    total,
    page,
    pageSize,
    setPage,
    handlePageSizeChange,
    isEmpty: !isCountLoading && providersCount === 0,
    hasNoResult: !isLoading && providersCount > 0 && total === 0,
    noResultSubtitle,
    showSearch: providersCount >= SEARCH_MIN_PROVIDERS,
    showFilters,
    showPagination: total > DEFAULT_PAGE_SIZE,
    getProviderHref,
    getProviderClickHandler,
  };
}
